import { create } from "zustand";
import { persist } from "zustand/middleware";

// ═══════════════════════════════════════════════════════════
// UIStore — Global UI state (sidebar, onboarding)
// ═══════════════════════════════════════════════════════════

/** Stable IDs for each onboarding step — used as keys in `completedOnboardingSteps`. */
export type OnboardingStepId = "rules" | "settings" | "dashboard";

interface UIState {
  sidebarOpen: boolean;
  theme: "light" | "dark" | "system";
  profileCollapsed: boolean;
  onboardingOpen: boolean;
  hasSeenOnboarding: boolean;
  /** Step IDs the user has actually visited via the onboarding guide — a real tracked checklist,
   *  not just a one-time "have they seen the modal at all" flag. */
  completedOnboardingSteps: OnboardingStepId[];

  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setTheme: (theme: "light" | "dark" | "system") => void;
  toggleTheme: () => void;
  toggleProfileCollapsed: () => void;

  setOnboardingOpen: (open: boolean) => void;
  markOnboardingSeen: () => void;
  markOnboardingStepComplete: (step: OnboardingStepId) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      theme: "light",
      profileCollapsed: false,
      onboardingOpen: false,
      hasSeenOnboarding: false,
      completedOnboardingSteps: [],

      toggleSidebar: () => {
        set((s) => ({ sidebarOpen: !s.sidebarOpen }));
      },
      setSidebarOpen: (open) => {
        set({ sidebarOpen: open });
      },
      setTheme: (theme) => {
        set({ theme });
      },
      toggleTheme: () => {
        set((s) => ({
          theme: s.theme === "light" ? "dark" : "light",
        }));
      },
      toggleProfileCollapsed: () => {
        set((s) => ({ profileCollapsed: !s.profileCollapsed }));
      },

      setOnboardingOpen: (open) => {
        set({ onboardingOpen: open });
      },
      markOnboardingSeen: () => {
        set({ hasSeenOnboarding: true });
      },
      markOnboardingStepComplete: (step) => {
        set((s) =>
          s.completedOnboardingSteps.includes(step)
            ? s
            : { completedOnboardingSteps: [...s.completedOnboardingSteps, step] },
        );
      },
    }),
    {
      name: "ui-store",
      partialize: (s) => ({
        sidebarOpen: s.sidebarOpen,
        theme: s.theme,
        profileCollapsed: s.profileCollapsed,
        hasSeenOnboarding: s.hasSeenOnboarding,
        completedOnboardingSteps: s.completedOnboardingSteps,
      }),
    },
  ),
);
